import { z } from "zod";
export const VastQuerySchema = z.object({
    device_id: z.string().optional(),
    user_id: z.string().optional(),
    cat: z.union([z.string(), z.array(z.string())]).optional(),
    channel_id: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    devicetype: z.string().optional(),
    h: z.string().optional(),
    w: z.string().optional(),
    ifa: z.string().optional(),
    ip: z.string().optional(),
    lat: z.string().optional(),
    lon: z.string().optional(),
    livestream: z.string().optional(),
    max_duration: z.string().optional(),
    min_duration: z.string().optional(),
    model: z.string().optional(),
    ott_id: z.string().optional(),
    region: z.string().optional(),
    session_duration: z.string().optional(),
    ua: z.string().optional(),
    age: z.string().optional(),
    age_group: z.union([z.string(), z.array(z.string())]).optional(),
    gender: z.string().optional(),
    wellness: z.string().optional(),
    habbits: z.union([z.string(), z.array(z.string())]).optional(),
});
export const ImpressionQuerySchema = z.object({
    uuid: z.string().optional(),
});
export const ImportSourceSchema = z.enum(["vast", "rtb"]);
export const ImportCampaignRequestSchema = z.object({
    type: ImportSourceSchema,
    url: z.string().regex(/^https?:\/\/.+/i, "Must be an http(s) URL"),
});
export const ImportCampaignDataSchema = z.object({
    name: z.string().nullable(),
    description: z.string().nullable(),
    creativeUrl: z.string().nullable(),
    creativeWidth: z.number().nullable(),
    creativeHeight: z.number().nullable(),
    creativeDuration: z.number().nullable(),
});
// --- SSRF guard for transit-campaign entrypoint URLs -----------------------
//
// Pure, dependency-free check: is `hostname` a literal IP in a
// private/loopback/link-local range (or `localhost`)? Non-IP hostnames are
// treated as NOT private here — DNS-resolution rejection is enforced later at
// fan-out time (out of scope for this Zod schema). `hostname` is expected in
// the form returned by `URL.hostname` (IPv6 is un-bracketed there).
export function isPrivateOrLoopbackHost(hostname) {
    const host = hostname.toLowerCase();
    if (host === "localhost")
        return true;
    // Strip surrounding brackets if a caller passed a bracketed IPv6 literal.
    const bare = host.startsWith("[") && host.endsWith("]") ? host.slice(1, -1) : host;
    // IPv6.
    if (bare.includes(":")) {
        if (bare === "::1" || bare === "::")
            return true; // loopback / unspecified
        // IPv4-mapped/embedded forms (::ffff:a.b.c.d, ::a.b.c.d) — extract the
        // trailing dotted IPv4 and re-check it (e.g. ::ffff:169.254.169.254).
        const dotted = bare.match(/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/);
        if (dotted)
            return isPrivateOrLoopbackHost(dotted[1]);
        // IPv4-mapped (::ffff:aabb:ccdd) or IPv4-compatible (::aabb:ccdd) hex form —
        // reassemble the embedded IPv4 and re-check. `ffff:` is optional so the
        // deprecated IPv4-compatible ::a.b.c.d form cannot slip past the guard.
        const hex = bare.match(/^::(?:ffff:)?([0-9a-f]{1,4}):([0-9a-f]{1,4})$/i);
        if (hex) {
            const hi = parseInt(hex[1], 16);
            const lo = parseInt(hex[2], 16);
            const v4 = `${(hi >> 8) & 0xff}.${hi & 0xff}.${(lo >> 8) & 0xff}.${lo & 0xff}`;
            return isPrivateOrLoopbackHost(v4);
        }
        // Unique-local (fc00::/7 -> fc/fd) and link-local (fe80::/10 -> fe8..feb).
        const firstHextet = bare.split(":").find((h) => h.length > 0) ?? "";
        const p2 = firstHextet.slice(0, 2).toLowerCase();
        if (p2 === "fc" || p2 === "fd")
            return true;
        if (p2 === "fe" &&
            ["8", "9", "a", "b"].includes(firstHextet.charAt(2).toLowerCase())) {
            return true; // fe80::/10 link-local
        }
        // Unknown/other IPv6 (incl. IPv4-mapped we could not parse) — fail closed.
        return firstHextet === "" ? true : false;
    }
    // IPv4: must be exactly four numeric octets in 0..255.
    const parts = bare.split(".");
    if (parts.length !== 4)
        return false;
    const octets = [];
    for (const part of parts) {
        if (!/^\d{1,3}$/.test(part))
            return false;
        const n = Number(part);
        if (n > 255)
            return false;
        octets.push(n);
    }
    const [a, b] = octets;
    if (a === 0)
        return true; // 0.0.0.0/8 (covers the literal 0.0.0.0)
    if (a === 127)
        return true; // 127.0.0.0/8 loopback
    if (a === 10)
        return true; // 10.0.0.0/8 private
    if (a === 172 && b >= 16 && b <= 31)
        return true; // 172.16.0.0/12 private
    if (a === 192 && b === 168)
        return true; // 192.168.0.0/16 private
    if (a === 169 && b === 254)
        return true; // 169.254.0.0/16 link-local
    return false;
}
// Entrypoint URL for a transit (RTB) campaign. Must be an absolute https URL
// that does not point at a private/loopback/link-local host. This is a
// SECURITY-CRITICAL SSRF guard.
export const EntrypointUrlSchema = z
    .string()
    .url()
    .superRefine((value, ctx) => {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        ctx.addIssue({
            code: "custom",
            message: "Invalid URL",
        });
        return;
    }
    if (url.protocol !== "https:") {
        ctx.addIssue({
            code: "custom",
            message: "Entrypoint URL must use https",
        });
    }
    if (isPrivateOrLoopbackHost(url.hostname)) {
        ctx.addIssue({
            code: "custom",
            message: "Entrypoint URL must not target a private or loopback host",
        });
    }
});
export const CampaignImportMetadataSchema = z.object({
    source: ImportSourceSchema,
    url: z.string(),
    importedAt: z.string(),
    parsed: ImportCampaignDataSchema,
    raw: z.string(),
    impressionDomain: z.string().nullable(),
});
//# sourceMappingURL=vast.js.map